import React, { useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { RemoveScroll } from 'react-remove-scroll';

import { FloatingOverlay } from '@floating-ui/react';

import { overrideProperties } from '@ecp/common/src/style/utils';
import { maxSizePropTypes, paddingPropTypes } from '@ecp/common/src/style/propTypes';
import { zIndex } from '@ecp/common/src/style/constant';
import boTheme from '@ecp/common/src/style/theme/bo';
import ModalUnstyled from '@ecp/common/src/components/unstyled/floating/ModalUnstyled';
import { modalTypeClassName } from '@ecp/common/src/components/unstyled/floating/useModal';
import { getPositionProp, modalRootProps, ModalWrapperStyle } from '@ecp/common/src/components/modal/Modal';
import { ReactComponent as CloseIcon } from '@ecp/common/src/assets/icon/icon__bo__close.svg';

const StyledModalRoot = React.forwardRef(({ children, zIndex: zIndexProp, ...props }, ref) => {
  const rootRef = useRef(null);
  useImperativeHandle(ref, () => rootRef.current);

  const { className } = props;
  return (
    <RemoveScroll>
      <FloatingOverlay
        data-testid="floating-overlay"
        style={{
          zIndex: zIndexProp ?? zIndex.modal,
          display: 'grid',
          background: className.includes(modalTypeClassName.inner) ? 'transparent' : 'rgba(0, 0, 0, 0.5)',
          ...getPositionProp(className),
        }}
      >
        <ModalWrapper ref={rootRef} {...props}>
          {children}
        </ModalWrapper>
      </FloatingOverlay>
    </RemoveScroll>
  );
});
StyledModalRoot.displayName = 'StyledModalRoot';

StyledModalRoot.propTypes = {
  zIndex: PropTypes.number,
  children: PropTypes.node,
  className: PropTypes.arrayOf(PropTypes.string),
};

const Modal = (props) => (
  <ModalUnstyled
    openWhenClicked
    rootComponent={StyledModalRoot}
    rootProps={modalRootProps(props)}
    {...props}
    {...{
      closeComponent: props.closeComponent ?? (
        <DefaultCloseComponent
          onClick={() => {
            props.onClose && props.onClose();
          }}
        />
      ),
    }}
  />
);

Modal.displayName = 'Modal';

Modal.propTypes = ModalUnstyled.propTypes;

export default Modal;

const ModalWrapper = styled(ModalWrapperStyle)`
  ${overrideProperties(maxSizePropTypes, { maxWidth: 'none', maxHeight: '740px' })}

  ${overrideProperties(paddingPropTypes, { padding: '16px 20px 24px 20px' })}

  &.center {
    border: 1px solid ${boTheme.color.menu1};
    box-shadow: 2px 2px 6px rgba(76, 75, 100, 0.2);
  }
`;

const DefaultCloseComponent = styled(CloseIcon)`
  border: none;
  width: 16px;
  height: 16px;
  background-color: #ffffff;
  position: inherit;
  top: 16px;
  right: 20px;
  cursor: pointer;
  & > * {
    fill: ${boTheme.color.sub1};
  }
`;
